import { useCallback, useState } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { CoaEditorCommand } from "./coaEditorCommand";
import { NestedEditorRow } from "./nestedRowTypes";
import {
    DeleteRowUnit,
    orderDeleteUnits,
    resolveWorkspaceDeleteCopy,
    toDeleteRowUnit,
    uniqueEntityIds,
} from "./nestedRowDeleteUnits";
import {
    NestedEditorHost,
    NestedRowGesture,
    NestedRowRefusal,
} from "./nestedRowRefusal";

// Id lists rather than single ids: a merged row deletes every business's row.
// The workspace variants keep the display-row split, because each row is its
// own transaction there.
type PendingDelete =
    | { kind: "account"; accountIds: string[] }
    | { kind: "group"; groupIds: string[] }
    | { kind: "bulk"; accountIds: string[]; groupIds: string[] }
    | { kind: "workspaceRow"; unit: DeleteRowUnit }
    | { kind: "workspaceBulk"; units: DeleteRowUnit[] };

interface DeleteRequest {
    ids: string[];
    row: NestedEditorRow | null;
}

interface UseNestedEditorRowActionsParams {
    setAccountDisabled: (id: string, isDisabled: boolean) => void;
    deleteAccount: (id: string) => void;
    deleteGroup: (id: string) => void;
    selectedVisibleAccountIds: string[];
    deletableSelectedAccountIds: string[];
    disablableSelectedAccountIds: string[];
    // Bulk Delete includes groups so a group-only selection is not a dead end.
    // Constituent draft group ids (not display-key row ids).
    selectedGroupIds: string[];
    clearSelection: () => void;
    accountNoun: "template" | "chart";
    host: NestedEditorHost;
    // Workspace only: the selected DISPLAY rows, so a bulk gesture splits into
    // one command per row instead of one flat id list.
    selectedRows?: NestedEditorRow[];
    resolveRowRefusal?: (params: {
        row: NestedEditorRow;
        gesture: NestedRowGesture;
    }) => NestedRowRefusal[];
    onRowRefused?: (refusals: NestedRowRefusal[]) => void;
    businessNamesFor?: (entityIds: number[]) => string[];
    onOperation?: (command: CoaEditorCommand) => void;
}

export interface NestedEditorRowActions {
    pendingDelete: PendingDelete | null;
    canBulkDelete: boolean;
    deleteModalTitle: string;
    deleteModalQuestion: string;
    handleConfirmDelete: () => void;
    closeDelete: () => void;
    requestDeleteAccount: (request: DeleteRequest) => void;
    requestDeleteGroup: (request: DeleteRequest) => void;
    handleBulkEnable: () => void;
    handleBulkDisable: () => void;
    handleBulkDelete: () => void;
}

const NO_SELECTED_ROWS: NestedEditorRow[] = [];

interface DeleteCopy {
    title: string;
    question: string;
}

const describeDeleteTargets = (
    accountCount: number,
    groupCount: number,
): string => {
    const clauses: string[] = [];
    if (accountCount > 0) {
        clauses.push(pluralizeWithCount("account", accountCount));
    }
    if (groupCount > 0) {
        clauses.push(pluralizeWithCount("group", groupCount));
    }
    return clauses.join(" and ");
};

// A display row's constituents share `isDisabled` (it is part of the display
// key), so a row is wholly archived or wholly enabled.
const isRowArchived = (row: NestedEditorRow): boolean =>
    row.accounts.length > 0 &&
    row.accounts.every((account) => account.isDisabled);

export const useNestedEditorRowActions = ({
    setAccountDisabled,
    deleteAccount,
    deleteGroup,
    selectedVisibleAccountIds,
    deletableSelectedAccountIds,
    disablableSelectedAccountIds,
    selectedGroupIds,
    clearSelection,
    accountNoun,
    host,
    selectedRows,
    resolveRowRefusal,
    onRowRefused,
    businessNamesFor,
    onOperation,
}: UseNestedEditorRowActionsParams): NestedEditorRowActions => {
    const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
        null,
    );
    const isWorkspaceHost = host === "workspace";
    const rows = selectedRows ?? NO_SELECTED_ROWS;

    // Templates keep the all-or-nothing gate. On the workspace chart a blocked
    // row refuses itself and the rest of the selection still proceeds.
    const canBulkDelete = isWorkspaceHost
        ? rows.length > 0
        : deletableSelectedAccountIds.length ===
              selectedVisibleAccountIds.length &&
          (deletableSelectedAccountIds.length > 0 ||
              selectedGroupIds.length > 0);

    const closeDelete = useCallback(() => setPendingDelete(null), []);

    const refuseRow = useCallback(
        (row: NestedEditorRow, gesture: NestedRowGesture): boolean => {
            const refusals = resolveRowRefusal?.({ row, gesture }) ?? [];
            if (refusals.length === 0) {
                return false;
            }
            onRowRefused?.(refusals);
            return true;
        },
        [onRowRefused, resolveRowRefusal],
    );

    const requestDelete = useCallback(
        ({ ids, row }: DeleteRequest, kind: "account" | "group") => {
            if (isWorkspaceHost && row !== null) {
                if (refuseRow(row, "delete")) {
                    return;
                }
                const unit = toDeleteRowUnit(row);
                if (unit !== null) {
                    setPendingDelete({ kind: "workspaceRow", unit });
                }
                return;
            }
            setPendingDelete(
                kind === "account"
                    ? { kind: "account", accountIds: ids }
                    : { kind: "group", groupIds: ids },
            );
        },
        [isWorkspaceHost, refuseRow],
    );

    const requestDeleteAccount = useCallback(
        (request: DeleteRequest) => requestDelete(request, "account"),
        [requestDelete],
    );
    const requestDeleteGroup = useCallback(
        (request: DeleteRequest) => requestDelete(request, "group"),
        [requestDelete],
    );

    const applyDeleteUnit = useCallback(
        (unit: DeleteRowUnit) => {
            if (unit.kind === "account") {
                unit.ids.forEach((id) => deleteAccount(id));
                onOperation?.({
                    type: "delete",
                    accountIds: unit.ids,
                    groupIds: [],
                });
                return;
            }
            unit.ids.forEach((id) => deleteGroup(id));
            onOperation?.({
                type: "delete",
                accountIds: [],
                groupIds: unit.ids,
            });
        },
        [deleteAccount, deleteGroup, onOperation],
    );

    const handleConfirmDelete = useCallback(() => {
        if (pendingDelete === null) {
            return;
        }
        if (pendingDelete.kind === "workspaceRow") {
            applyDeleteUnit(pendingDelete.unit);
        } else if (pendingDelete.kind === "workspaceBulk") {
            orderDeleteUnits(pendingDelete.units).forEach(applyDeleteUnit);
            clearSelection();
        } else if (pendingDelete.kind === "account") {
            pendingDelete.accountIds.forEach((id) => deleteAccount(id));
            onOperation?.({
                type: "delete",
                accountIds: pendingDelete.accountIds,
                groupIds: [],
            });
        } else if (pendingDelete.kind === "group") {
            pendingDelete.groupIds.forEach((id) => deleteGroup(id));
            onOperation?.({
                type: "delete",
                accountIds: [],
                groupIds: pendingDelete.groupIds,
            });
        } else {
            // deleteGroup re-parents surviving children, so accounts go first.
            pendingDelete.accountIds.forEach((id) => deleteAccount(id));
            pendingDelete.groupIds.forEach((id) => deleteGroup(id));
            onOperation?.({
                type: "delete",
                accountIds: pendingDelete.accountIds,
                groupIds: pendingDelete.groupIds,
            });
            clearSelection();
        }
        setPendingDelete(null);
    }, [
        applyDeleteUnit,
        clearSelection,
        deleteAccount,
        deleteGroup,
        onOperation,
        pendingDelete,
    ]);

    // One command per display row: a failure mid-queue leaves earlier rows
    // landed and the rest unsent, which is the documented bulk behaviour.
    const runWorkspaceBulkDisabled = useCallback(
        (gesture: "archive" | "enable") => {
            const isDisabled = gesture === "archive";
            for (const row of rows) {
                if (row.kind !== "account" || row.accounts.length === 0) {
                    continue;
                }
                if (isRowArchived(row) === isDisabled) {
                    continue;
                }
                if (isDisabled && refuseRow(row, "archive")) {
                    continue;
                }
                const accountIds = row.accounts.map((account) => account.id);
                accountIds.forEach((id) => setAccountDisabled(id, isDisabled));
                onOperation?.({
                    type: "setAccountDisabled",
                    accountIds,
                    isDisabled,
                });
            }
            clearSelection();
        },
        [clearSelection, onOperation, refuseRow, rows, setAccountDisabled],
    );

    const handleBulkEnable = useCallback(() => {
        if (isWorkspaceHost) {
            runWorkspaceBulkDisabled("enable");
            return;
        }
        if (selectedVisibleAccountIds.length === 0) {
            return;
        }
        selectedVisibleAccountIds.forEach((id) =>
            setAccountDisabled(id, false),
        );
        onOperation?.({
            type: "setAccountDisabled",
            accountIds: selectedVisibleAccountIds,
            isDisabled: false,
        });
        clearSelection();
    }, [
        clearSelection,
        isWorkspaceHost,
        onOperation,
        runWorkspaceBulkDisabled,
        selectedVisibleAccountIds,
        setAccountDisabled,
    ]);

    const handleBulkDisable = useCallback(() => {
        if (isWorkspaceHost) {
            runWorkspaceBulkDisabled("archive");
            return;
        }
        if (disablableSelectedAccountIds.length === 0) {
            return;
        }
        disablableSelectedAccountIds.forEach((id) =>
            setAccountDisabled(id, true),
        );
        onOperation?.({
            type: "setAccountDisabled",
            accountIds: disablableSelectedAccountIds,
            isDisabled: true,
        });
        clearSelection();
    }, [
        clearSelection,
        disablableSelectedAccountIds,
        isWorkspaceHost,
        onOperation,
        runWorkspaceBulkDisabled,
        setAccountDisabled,
    ]);

    const handleBulkDelete = useCallback(() => {
        if (!canBulkDelete) {
            return;
        }
        if (!isWorkspaceHost) {
            setPendingDelete({
                kind: "bulk",
                accountIds: deletableSelectedAccountIds,
                groupIds: selectedGroupIds,
            });
            return;
        }
        const units: DeleteRowUnit[] = [];
        for (const row of rows) {
            if (refuseRow(row, "delete")) {
                continue;
            }
            const unit = toDeleteRowUnit(row);
            if (unit !== null) {
                units.push(unit);
            }
        }
        // Every selected row refused: each one already said why, and there is
        // nothing left to confirm.
        if (units.length === 0) {
            return;
        }
        setPendingDelete({ kind: "workspaceBulk", units });
    }, [
        canBulkDelete,
        deletableSelectedAccountIds,
        isWorkspaceHost,
        refuseRow,
        rows,
        selectedGroupIds,
    ]);

    const resolveDeleteCopy = (): DeleteCopy => {
        if (pendingDelete?.kind === "workspaceRow") {
            return resolveWorkspaceDeleteCopy({
                kind: pendingDelete.unit.kind,
                businessNames:
                    businessNamesFor?.(pendingDelete.unit.entityIds) ?? [],
                name: pendingDelete.unit.name,
            });
        }
        if (pendingDelete?.kind === "workspaceBulk") {
            return resolveWorkspaceDeleteCopy({
                kind: "bulk",
                businessNames:
                    businessNamesFor?.(uniqueEntityIds(pendingDelete.units)) ??
                    [],
            });
        }
        if (pendingDelete?.kind === "group") {
            return {
                title: "Delete group?",
                question:
                    "This removes only the group. Its accounts and subgroups move up one level.",
            };
        }
        if (pendingDelete?.kind === "account") {
            return {
                title: "Delete account?",
                question: `This will remove this account from the ${accountNoun}.`,
            };
        }
        const accountCount =
            pendingDelete?.kind === "bulk"
                ? pendingDelete.accountIds.length
                : 0;
        const groupCount =
            pendingDelete?.kind === "bulk" ? pendingDelete.groupIds.length : 0;
        const targets = describeDeleteTargets(accountCount, groupCount);
        return {
            title: `Delete ${targets}?`,
            question: `This will remove ${targets} from the ${accountNoun}.`,
        };
    };
    const { title: deleteModalTitle, question: deleteModalQuestion } =
        resolveDeleteCopy();

    return {
        pendingDelete,
        canBulkDelete,
        deleteModalTitle,
        deleteModalQuestion,
        handleConfirmDelete,
        closeDelete,
        requestDeleteAccount,
        requestDeleteGroup,
        handleBulkEnable,
        handleBulkDisable,
        handleBulkDelete,
    };
};
